import { DateOnly } from '@if/core/date'
import { formatCurrency } from '@if/core/utils'
import { BodyText, Icon, useDialog } from '@if/mui'
import { Box, Chip, Stack } from '@mui/material'
import { useCalculateAmountToSpread } from '@src/api/loan/loan-api'
import MultiLineChip from '@src/components/Common/Chip/MultiLineChip'
import ReturnToCalendarAction, {
  RETURN_TO_CALENDAR,
  ToolResult,
} from '@src/components/LoanCalendar/EditPayments/EditPayments-tool'
import { SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { ArrowRight, Split } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * What spreading the overdue over the payments in scope would cost — worked out before the
 * dialog opens, so the body never renders a figure that is still being computed.
 */
interface SpreadOverdueAmountPreview {
  paymentCount: number
  amountToRaise: number
  originalLastPaymentDate: DateOnly
  originalLastPaymentAmount: number
  newLastPaymentDate: DateOnly
  newLastPaymentAmount: number
}

const getChipColor = (originalAmount: number, newAmount: number) => {
  if (originalAmount > newAmount) return 'success'
  if (originalAmount < newAmount) return 'error'
  return 'default'
}

/**
 * The one tool with nothing to fill in: the backend answers what each selected payment has to
 * rise by, and Confirm applies that. Opened through the load thunk, so the request is the
 * dialog's opening phase rather than an effect firing inside an already-open body.
 */
export const useLoanSpreadOverdueAmountDialog = (initialSignedSimulation: SignedCalendarSimulation) => {
  const { t } = useTranslation()
  const { loan } = initialSignedSimulation.simulation.fullLoan
  const { mutateAsync: calculateAmount } = useCalculateAmountToSpread(loan.id, loan.financingCompanyId)

  const dialog = useDialog<SpreadOverdueAmountPreview, ToolResult<number>>({
    icon: Split,
    title: t('loan.payment.spreadOverdueAmountDialog.title'),
    confirmLabel: t('common.confirm'),
    maxWidth: 'sm',
    fullWidth: true,
    confirmDisabled: (ctx) => ctx.props.amountToRaise <= 0,
    additionalActions: (ctx) => <ReturnToCalendarAction onReturn={() => ctx.close(RETURN_TO_CALENDAR)} />,
    content: (ctx) => (
      <Stack>
        <BodyText sx={{ textAlign: 'center' }}>
          {t('loan.payment.spreadOverdueAmountDialog.amountToAdd', { paymentNb: ctx.props.paymentCount })}
        </BodyText>
        <Stack direction="row" sx={{ alignSelf: 'center', m: 1 }}>
          <Chip label={formatCurrency(ctx.props.amountToRaise)} size="small" color="success" />
        </Stack>
        <BodyText sx={{ textAlign: 'center', m: 1 }}>
          {t('loan.payment.spreadOverdueAmountDialog.newLastPayment')}
        </BodyText>
        <Stack direction="row" spacing={1} sx={{ alignSelf: 'center', mb: 1 }}>
          <MultiLineChip
            label={`${ctx.props.originalLastPaymentDate.toString()}\n${formatCurrency(ctx.props.originalLastPaymentAmount)}`}
            variant="outlined"
          />
          <Box sx={{ pt: 1, color: 'text.disabled' }}>
            <Icon icon={ArrowRight} size="sm" />
          </Box>
          <MultiLineChip
            label={`${ctx.props.newLastPaymentDate.toString()}\n${formatCurrency(ctx.props.newLastPaymentAmount)}`}
            color={getChipColor(ctx.props.originalLastPaymentAmount, ctx.props.newLastPaymentAmount)}
          />
        </Stack>
      </Stack>
    ),
    onConfirm: (ctx) => ctx.props.amountToRaise,
  })

  const open = useCallback(
    (paymentDates: DateOnly[]) =>
      dialog.open(async () => {
        const { simulation } = initialSignedSimulation
        const paymentsDate = simulation.operationInformations
          .filter((oi) => paymentDates.some((date) => date.getTime() === oi.operation.effectiveDate.getTime()))
          .map((oi) => oi.operation.effectiveDate)

        const result = await calculateAmount({ paymentsDate, simulation })
        const originalLastPayment = simulation.customerPaymentOperations
          .filter((p) => p.operation.effectiveDate <= simulation.fullLoan.loan.lastPaymentOn)
          .at(-1)?.operation

        // The dialog is built around what the last payment becomes, so there is nothing to show
        // without one. Saying which calendar it is beats the bare TypeError the `!` used to throw.
        if (!originalLastPayment)
          throw new Error(`Loan ${simulation.fullLoan.loan.contractNumber} has no payment on or before its end date`)

        return {
          paymentCount: paymentDates.length,
          amountToRaise: result.amountToRaise,
          originalLastPaymentDate: originalLastPayment.effectiveDate,
          originalLastPaymentAmount: originalLastPayment.amount,
          newLastPaymentDate: result.lastPaymentDate,
          newLastPaymentAmount: result.lastPaymentValue,
        }
      }),
    [calculateAmount, dialog, initialSignedSimulation],
  )

  return useMemo(() => ({ open }), [open])
}
